'use client';

import { useScrollTopOnChange } from '@/common/hooks/useScrollTopOnChange';
import { fullPageLoadingIndicator } from '@/components/LoadingIndicator';
import Pagination from '@/components/Pagination';
import { useReviewsPage } from '@/hooks/ReviewsPage/useReviewsPage';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { Select } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import ReviewItem from './reviewItem';
import classes from './reviewsPage.module.css';

const ImagesViewerModal = React.lazy(() => import('@/components/ReviewList/imagesViewerModal'));

const filterCategoryIds = [4, 5, 14, 16, 54, 20, 23, 9];

const ReviewsPage = props => {
  const { categories, filterCategory, filterCategoryName } = props;

  const talonProps = useReviewsPage(props);

  const { reviewdata, pageControl, loading, filterCategoryCount } = talonProps;

  useScrollTopOnChange(pageControl.currentPage);

  const router = useRouter();

  const isMobile = useIsMobile();

  const [reviewImages, setReviewImages] = useState([]);
  const imageViewerRef = useRef(null);

  const onItemImageClick = (images, index) => {
    const converted = images.map(item => {
      return {
        src: item.url.replace(/\/thumb_/, '/'),
      };
    });

    setReviewImages(converted);
    imageViewerRef?.current.setCurrentIndex(index);
    imageViewerRef?.current.setIsOpen(true);
  };

  let totalCount = 0;

  reviewdata?.categories.forEach(temp => {
    if (filterCategoryIds.includes(temp.id)) {
      totalCount += temp.count;
    }
  });

  const handleChange = id => {
    if (id === 2) {
      globalThis.open('/reviews');
    } else {
      const findCategory = categories.find(temp => temp.id === id);

      globalThis.open(`/reviews/category/${findCategory.url_key}`);
    }
  };

  const onItemClick = useCallback(
    item => {
      router.push(`/${item.product.url_key}.html`);
    },
    [router],
  );

  const maybePagination =
    reviewdata?.items.length > 0 ? (
      <div className={classes.pagination}>
        <Pagination pageControl={pageControl} />
      </div>
    ) : null;

  const reviewList = useMemo(() => {
    return reviewdata?.items
      .filter(temp => !!temp)
      .map((item, index) => (
        <ReviewItem
          key={index}
          item={item}
          onItemClick={onItemClick}
          onItemImageClick={onItemImageClick}
        />
      ));
  }, [onItemClick, reviewdata?.items]);

  if (loading) {
    return fullPageLoadingIndicator;
  }

  let options = categories
    .filter(temp => temp.id !== 21 && filterCategoryIds.includes(temp.id))
    .map(temp => {
      const count = reviewdata?.categories?.find(x => x.id === temp.id)?.count;
      return {
        value: temp.id,
        label: `${temp.name}(${count || 0})`,
      };
    });

  options = [
    {
      label: `All Reviews (${totalCount} reviews)`,
      value: 2,
    },
    ...options,
  ];

  const categorySelect =
    isMobile && options.length > 0 ? (
      <Select
        className={classes.mobileSelect}
        defaultValue={filterCategory}
        onChange={handleChange}
        options={options}
      />
    ) : null;

  return (
    <div className={cn('max-w-1440px', 'm-center mb-10 mt-[56px]', 'lg_flex lg_flex-wrap lg_justify-between')}>
      {!isMobile && (
        <div className='mr-[90px] w-[280px]'>
          <h1 className={cn('font-Shango text-base ', 'border-b border-solid border-E7DED5', 'pb-2.5')}>
            Reviews by Category
          </h1>
          <ul className={cn('mt-4 text-[15px]')}>
            <li className={cn('mb-3 cursor-pointer', 'block pb-[5px] hover_text-theme')}>
              <Link
                href='/reviews'
                className={cn('hover_underline', filterCategory === 2 && 'text-theme underline')}
              >
                All Reviews ({totalCount} reviews)
              </Link>
            </li>
            {categories
              .filter(temp => filterCategoryIds.includes(temp.id))
              .map((item, index) => {
                if (item.id === 2) {
                  return null;
                }

                const count = reviewdata?.categories?.find(temp => temp.id === item.id)?.count;

                if (!count) {
                  return null;
                }

                return (
                  <li
                    key={index}
                    className={cn('mb-3 cursor-pointer', 'block pb-[5px] hover_text-theme')}
                  >
                    <Link
                      href={`/reviews/category/${item.url_key}`}
                      className={cn(' hover_underline', filterCategory === item.id && 'text-theme underline')}
                    >
                      {item.id === 165
                        ? 'Sample Bridesmaid Dresses' + ' Reviews (' + count + ')'
                        : item.name + ' Reviews (' + count + ')'}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
      )}
      <div className={classes.rightreviewlist}>
        <h1
          className={cn(
            'text-center font-Shango text-base lg_text-left',
            'border-b border-solid border-E7DED5',
            'pb-2.5',
          )}
        >
          {filterCategory === 2 ? 'All' : filterCategoryName} Reviews{' '}
          {filterCategory === 2 ? totalCount : filterCategoryCount} reviews
        </h1>
        {categorySelect}
        <ul className='my-[17px] px-2.5 lg_mt-5'>{reviewList}</ul>
        {maybePagination}
      </div>
      <Suspense fallback={null}>
        <div className={classes.reviewimglist}>
          <ImagesViewerModal
            ref={imageViewerRef}
            images={reviewImages}
          />
        </div>
      </Suspense>
    </div>
  );
};

export default ReviewsPage;
